/**
 * Container Cash-in — bám `frmFuturesMainSubFormBank`:
 *   - Combobox Bank (Vietinbank / MBbank / BIDV, mặc định Vietinbank).
 *   - 3 nút New / Edit / Delete (Access gate bằng `SCREEN_ACCESS = 1`; FE dùng `canEdit`).
 *   - Swap subform theo bank: VTB/MBB → `FuturesBankCashInView`; BIDV → `FuturesBankCashInBIDVView`.
 */
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { Autocomplete, Box, Button, Stack, TextField } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { useState } from 'react'

import ConfirmDialog from '../../../components/ConfirmDialog'
import { usePagePermission } from '../../../hooks/usePagePermission'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { COMBOBOX_FILTER_COMPACT, HIDE_ON_MOBILE_INLINE } from '../../../theme/responsive'
import { useDeleteBankCashMutation } from '../../../store/api/futuresControlApi'
import { futuresApiErrorMessage } from '../futuresApiErrorMessage'
import BankCashInDialog, { type BankCashInRow, type BankName } from './BankCashInDialog'
import FuturesBankCashInBIDVView from './FuturesBankCashInBIDVView'
import FuturesBankCashInView from './FuturesBankCashInView'

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

// Bank picker = navigation/filter selector — dùng COMBOBOX_FILTER_COMPACT
// (transform scale xs để compact mà KHÔNG đụng input fontSize → no iOS zoom).
// readOnly trên renderInput TextField để mobile không bung keyboard (user chỉ
// chọn từ 3 options, không gõ).

const BANK_OPTIONS: BankName[] = ['Vietinbank', 'MBbank', 'BIDV']

const stableKey = (r: BankCashInRow): string =>
  `${String(r.bank ?? '')}|${String(r.date ?? '').slice(0, 10)}`

export default function FuturesBankCashInTab() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()
  const [delBankCash, delState] = useDeleteBankCashMutation()

  const [bank, setBank] = useState<BankName>('Vietinbank')
  const [selected, setSelected] = useState<BankCashInRow | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogRow, setDialogRow] = useState<BankCashInRow | null>(null)
  const [pendingDelete, setPendingDelete] = useState<BankCashInRow | null>(null)

  const onSelect = (row: BankCashInRow | null) => setSelected(row)

  const openNew = () => {
    setDialogRow(null)
    setDialogOpen(true)
  }
  const openEdit = () => {
    if (!selected) return
    setDialogRow(selected)
    setDialogOpen(true)
  }
  const askDelete = () => {
    if (!selected?.date) return
    setPendingDelete(selected)
  }

  const confirmDelete = async () => {
    if (!pendingDelete?.date) return
    try {
      await delBankCash({
        date: String(pendingDelete.date).slice(0, 10),
        bank: String(pendingDelete.bank ?? bank),
      }).unwrap()
      snack.success('Successfully Deleted')
      setSelected(null)
      setPendingDelete(null)
    } catch (err) {
      snack.error(futuresApiErrorMessage(err))
    }
  }

  const actions = canEdit ? (
    <>
      <Button
        variant="contained"
        size="small"
        color="success"
        sx={actionToolbarBtnSx}
        onClick={openNew}
        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
        title="New"
      >
        <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
      </Button>
      <Button
        variant="outlined"
        size="small"
        color="warning"
        sx={actionToolbarBtnSx}
        disabled={!selected}
        onClick={openEdit}
        startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
        title="Edit"
      >
        <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
      </Button>
      <Button
        variant="outlined"
        size="small"
        color="error"
        sx={actionToolbarBtnSx}
        disabled={!selected}
        onClick={askDelete}
        startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
        title="Delete"
      >
        <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
      </Button>
    </>
  ) : null

  // Stack header với bank selector + slot actions.
  const headerActions = (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
      <Autocomplete
        size="small"
        autoHighlight
        disableClearable
        options={BANK_OPTIONS}
        value={bank}
        onChange={(_, v) => {
          setBank(v)
          setSelected(null)
        }}
        isOptionEqualToValue={(a, b) => a === b}
        sx={COMBOBOX_FILTER_COMPACT}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label="Bank"
            // readOnly: mobile không bung keyboard (Pattern 14 nav picker)
            inputProps={{ ...params.inputProps, readOnly: true }}
          />
        )}
      />
      {actions}
    </Stack>
  )

  const selectedKey = selected ? stableKey(selected) : null

  return (
    <>
      {bank === 'BIDV' ? (
        <FuturesBankCashInBIDVView
          bank="BIDV"
          selectedKey={selectedKey}
          onSelectionChange={onSelect}
          actionsSlot={headerActions}
        />
      ) : (
        <FuturesBankCashInView
          bank={bank}
          selectedKey={selectedKey}
          onSelectionChange={onSelect}
          actionsSlot={headerActions}
        />
      )}

      <BankCashInDialog
        open={dialogOpen}
        bank={bank}
        row={dialogRow}
        onClose={() => setDialogOpen(false)}
        onSaved={() => setSelected(null)}
        canEdit={canEdit}
      />

      <ConfirmDialog
        open={pendingDelete != null}
        title="Delete Bank Cash-in"
        message={
          pendingDelete
            ? `Do you want to delete cash of bank = '${pendingDelete.bank ?? bank}' with timing = '${String(pendingDelete.date ?? '').slice(0, 10)}' ?`
            : ''
        }
        danger
        loading={delState.isLoading}
        cancelLabel="Cancel"
        confirmLabel="Delete"
        onCancel={() => setPendingDelete(null)}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
      />
    </>
  )
}
